@if (lookups.length) {
  <div class="lookup-definitions__head" aria-hidden="true">
    <span>{{ 'TRANS__IMPORT__LOOKUPS__COLUMN_LOOKUP' | translate }}</span>
    <span>{{ 'TRANS__IMPORT__LOOKUPS__COLUMN_FLOW' | translate }}</span>
    <span>{{ 'TRANS__IMPORT__LOOKUPS__COLUMN_HITS' | translate }}</span>
    <span>{{ 'TRANS__IMPORT__LOOKUPS__COLUMN_USED_BY' | translate }}</span>
    <span></span>
  </div>
}

@for (lookup of lookups; track lookup.key) {
  @let open = expanded === lookup.key;
  @let check = checkOf(lookup);
  @let name = displayName(lookup);
  <div class="lookup-definitions__lookup" [class.lookup-definitions__lookup--open]="open">
    <div class="lookup-definitions__line">
      <span class="lookup-definitions__name">
        <mat-icon aria-hidden="true">key</mat-icon>
        {{ name || ('TRANS__IMPORT__LOOKUPS__UNNAMED' | translate) }}
      </span>
      <span class="lookup-definitions__flow">
        <span class="lookup-definitions__column-name">{{ lookup.codeColumnControl.value ?? '–' }}</span>
        <mat-icon aria-hidden="true">arrow_forward</mat-icon>
        <span class="lookup-definitions__table-name">{{ lookup.tableControl.value ?? '–' }}</span>
        @if (lookup.keyColumnControl.value) {
          <span class="lookup-definitions__key-column">.{{ lookup.keyColumnControl.value }}</span>
        }
      </span>
      <span class="lookup-definitions__hits" [class.lookup-definitions__stale]="check && checkStale">
        @if (columnProblems.get(lookup.key); as problem) {
          <span class="lookup-definitions__tag lookup-definitions__tag--warn lookup-definitions__column-missing">
            <mat-icon aria-hidden="true">warning</mat-icon>
            {{ 'TRANS__IMPORT__LOOKUPS__COLUMN_MISSING' | translate: {column: problem.column, table: problem.table} }}
          </span>
        } @else if (check) {
          <span class="lookup-definitions__tag"
                [class.lookup-definitions__tag--ok]="!check.missed_rows"
                [class.lookup-definitions__tag--warn]="check.missed_rows">
            <mat-icon aria-hidden="true">{{ check.missed_rows ? 'warning' : 'check' }}</mat-icon>
            {{ 'TRANS__IMPORT__LOOKUPS__HITS' | translate: {matched: check.matched_rows, rows: check.rows_with_code} }}
          </span>
        } @else if (!isComplete(lookup)) {
          <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__INCOMPLETE' | translate }}</span>
        } @else if (checkPending) {
          <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__CHECKING' | translate }}</span>
        }
      </span>
      <span class="lookup-definitions__usages">
        @if (usagesText(lookup); as usages) {
          {{ usages }}
        } @else {
          <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__UNUSED' | translate }}</span>
        }
      </span>
      <button
        mat-icon-button
        type="button"
        class="lookup-definitions__toggle"
        (click)="toggle(lookup)"
        [attr.aria-expanded]="open"
        [attr.aria-label]="(open ? 'TRANS__IMPORT__LOOKUPS__HIDE' : 'TRANS__IMPORT__LOOKUPS__SHOW') | translate: {name: name}"
      >
        <mat-icon>{{ open ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
    </div>

    @if (open) {
      <div class="lookup-definitions__details">
        <div class="lookup-definitions__fields">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lookup-definitions__field">
            <mat-label>{{ 'TRANS__IMPORT__LOOKUP_NAME' | translate }}</mat-label>
            <input matInput [formControl]="lookup.nameControl" [placeholder]="lookup.tableControl.value ?? ''">
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lookup-definitions__field">
            <mat-label>{{ 'TRANS__IMPORT__LOOKUP_SOURCE' | translate }}</mat-label>
            <mat-select [formControl]="lookup.codeColumnControl">
              @for (col of columns; track col.name) {
                <mat-option [value]="col.name">{{ col.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lookup-definitions__field">
            <mat-label>{{ 'TRANS__IMPORT__LOOKUP_TABLE' | translate }}</mat-label>
            <mat-select [formControl]="lookup.tableControl">
              @for (table of tables; track table) {
                <mat-option [value]="table">{{ table }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (lookup.tableControl.value) {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="lookup-definitions__field">
              <mat-label>{{ 'TRANS__IMPORT__LOOKUP_KEY_COLUMN' | translate }}</mat-label>
              <mat-select [formControl]="lookup.keyColumnControl">
                @for (col of keyColumns(lookup); track col.name) {
                  <mat-option [value]="col.name">{{ col.name }}</mat-option>
                }
              </mat-select>
              @if (isLoadingKeyColumns(lookup)) {
                <mat-hint>{{ 'TRANS__IMPORT__LOOKUP_LOADING_COLUMNS' | translate }}</mat-hint>
              }
            </mat-form-field>
          }
        </div>

        @if (columnsFailure(lookup); as failure) {
          <div class="lookup-definitions__failed">
            <span class="lookup-definitions__error">{{ failure }}</span>
            <button mat-stroked-button type="button" color="primary" class="lookup-definitions__retry-columns"
                    (click)="retryColumns.emit(lookup.tableControl.value!)">
              <span class="bold uppercase">{{ 'TRANS__IMPORT__LOOKUPS__RETRY' | translate }}</span>
            </button>
          </div>
        }

        @if (check) {
          @let checkedColumns = labelColumns(check);
          @let filter = codeFilter(lookup, check);
          @let capped = codesCapped(lookup, check);
          @let listedMisses = missRows(lookup, check).length;
          @let isCell = codeIsCell(lookup);
          <div class="lookup-definitions__box" [class.lookup-definitions__stale]="checkStale">
            <div class="lookup-definitions__box-head">
              <span class="lookup-definitions__box-title">{{ 'TRANS__IMPORT__LOOKUPS__CODES' | translate }}</span>
              <span class="lookup-definitions__muted lookup-definitions__codes-summary">
                {{ (capped ? 'TRANS__IMPORT__LOOKUPS__CODES_SUMMARY_CAPPED' : 'TRANS__IMPORT__LOOKUPS__CODES_SUMMARY') | translate: {
                  rows: check.rows_scanned, withCode: check.rows_with_code,
                  codes: check.codes.length, tableKeys: check.table_keys
                } }}
              </span>
              @if (checkPending) {
                <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__CHECKING' | translate }}</span>
              }
              <mat-button-toggle-group
                class="lookup-definitions__filter"
                [value]="filter"
                (change)="setCodeFilter(lookup, $event.value)"
                [attr.aria-label]="'TRANS__IMPORT__LOOKUPS__FILTER' | translate"
              >
                <mat-button-toggle value="all">
                  {{ 'TRANS__IMPORT__LOOKUPS__FILTER_ALL' | translate: {count: check.codes.length} }}
                </mat-button-toggle>
                <mat-button-toggle value="misses">
                  @if (capped) {
                    {{ 'TRANS__IMPORT__LOOKUPS__FILTER_MISSES_CAPPED' | translate: {shown: listedMisses, total: check.missed_code_count} }}
                  } @else {
                    {{ 'TRANS__IMPORT__LOOKUPS__FILTER_MISSES' | translate: {count: listedMisses} }}
                  }
                </mat-button-toggle>
              </mat-button-toggle-group>
            </div>

            <table class="lookup-definitions__codes">
              <thead>
                <ng-container
                  *ngTemplateOutlet="codeTableHead; context: {$implicit: 'TRANS__IMPORT__LOOKUPS__CODE', labelColumns: checkedColumns}"
                ></ng-container>
              </thead>
              <tbody>
                @for (row of shownCodeRows(lookup, check); track row.code) {
                  @if (row.missed) {
                    <tr class="lookup-definitions__miss">
                      <td class="lookup-definitions__mono lookup-definitions__code">{{ row.code }}</td>
                      <td class="lookup-definitions__count">{{ row.count }}</td>
                      <td>
                        @if (row.alias !== null) {
                          <span class="lookup-definitions__tried">
                            <span class="lookup-definitions__mono">{{ row.alias }}</span>
                            <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__ALIAS' | translate }}</span>
                          </span>
                        }
                        <span class="lookup-definitions__not-found">
                          <mat-icon aria-hidden="true">close</mat-icon>
                          {{ 'TRANS__IMPORT__LOOKUPS__NOT_IN_TABLE' | translate }}
                        </span>
                      </td>
                      <td [attr.colspan]="checkedColumns.length || 1">
                        @if (row.hiddenKey) {
                          <p class="lookup-definitions__note">
                            {{ 'TRANS__IMPORT__LOOKUPS__ALIAS_HIDES' | translate: {
                              code: row.code, label: keyLabelText(row.hiddenKey)
                            } }}
                            @if (isCell && !row.blankAlsoHits.length) {
                              {{ 'TRANS__IMPORT__LOOKUPS__MEANS_BLANK_HINT' | translate }}
                            }
                          </p>
                        }
                        @if (isPickerOpen(lookup, row.code)) {
                          <span class="lookup-definitions__picker-row">
                            <mat-form-field appearance="outline" subscriptSizing="dynamic"
                                            class="lookup-definitions__field lookup-definitions__picker">
                              <mat-label>{{ 'TRANS__IMPORT__LOOKUPS__PICK_KEY' | translate }}</mat-label>
                              <input matInput [formControl]="pickerControl" [matAutocomplete]="keyPicker">
                              <mat-autocomplete #keyPicker="matAutocomplete"
                                                (optionSelected)="pickTableKey(lookup, row.code, $event.option.value)">
                                @for (entry of pickerOptions(check); track entry.key) {
                                  <mat-option [value]="entry.key">
                                    <span class="lookup-definitions__mono">{{ entry.key }}</span>
                                    @if (keyLabelText(entry) !== entry.key) {
                                      <span class="lookup-definitions__muted">{{ keyLabelText(entry) }}</span>
                                    }
                                  </mat-option>
                                }
                              </mat-autocomplete>
                            </mat-form-field>
                            <button mat-icon-button type="button" class="lookup-definitions__picker-close"
                                    (click)="closePicker()"
                                    [attr.aria-label]="'TRANS__IMPORT__LOOKUPS__PICK_KEY_CANCEL' | translate">
                              <mat-icon>close</mat-icon>
                            </button>
                          </span>
                        } @else {
                          <button mat-stroked-button type="button" color="primary" class="lookup-definitions__pick-key"
                                  (click)="openPicker(lookup, row.code)">
                            <span class="bold uppercase">{{ 'TRANS__IMPORT__LOOKUPS__PICK_KEY' | translate }}</span>
                          </button>
                        }
                      </td>
                      <td>
                        <span class="lookup-definitions__actions">
                          @if (row.hiddenKey) {
                            <button mat-stroked-button type="button" color="primary"
                                    class="lookup-definitions__use-table-code"
                                    (click)="useTableCode(lookup, row.code)">
                              <span class="bold uppercase">{{ 'TRANS__IMPORT__LOOKUPS__USE_TABLE_CODE' | translate }}</span>
                              <span class="bold lookup-definitions__mono lookup-definitions__table-code">{{ row.hiddenKey.key }}</span>
                            </button>
                          }
                          @if (isCell) {
                            @if (row.blankAlsoHits.length) {
                              <span class="lookup-definitions__muted lookup-definitions__blank-case">
                                {{ 'TRANS__IMPORT__LOOKUPS__MEANS_BLANK_CASE' | translate: {codes: row.blankAlsoHits.join(', ')} }}
                              </span>
                            } @else {
                              <button mat-flat-button type="button" color="primary"
                                      class="lookup-definitions__means-blank"
                                      (click)="meansBlank.emit({lookup: lookup, code: row.code})">
                                <mat-icon>block</mat-icon>
                                <span class="bold uppercase">{{ 'TRANS__IMPORT__BLANK_VALUES__MEANS_BLANK' | translate }}</span>
                              </button>
                            }
                          }
                        </span>
                      </td>
                    </tr>
                  } @else {
                    <ng-container
                      *ngTemplateOutlet="hitRow; context: {$implicit: row, labelColumns: checkedColumns}"
                    ></ng-container>
                  }
                } @empty {
                  <tr>
                    <td [attr.colspan]="(checkedColumns.length || 1) + 4" class="lookup-definitions__muted">
                      @if (filter === 'all') {
                        {{ 'TRANS__IMPORT__LOOKUPS__NO_CODES' | translate }}
                      } @else if (capped && check.missed_code_count) {
                        {{ 'TRANS__IMPORT__LOOKUPS__NO_MISSES_CAPPED' | translate: {codes: check.codes.length} }}
                      } @else {
                        {{ 'TRANS__IMPORT__LOOKUPS__NO_MISSES' | translate }}
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>

            <div class="lookup-definitions__box-foot">
              @if (check.missed_rows) {
                <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__MISS_NOTE' | translate }}</span>
              }
              @if (check.table_keys > check.key_labels.length) {
                <span class="lookup-definitions__muted lookup-definitions__keys-capped">
                  {{ 'TRANS__IMPORT__LOOKUPS__KEYS_CAPPED' | translate: {shown: check.key_labels.length, total: check.table_keys} }}
                </span>
              }
              @if (hidesFilteredCodes(lookup, check) && filter === 'misses') {
                <button mat-button type="button" color="primary" class="lookup-definitions__show-all-misses"
                        (click)="showAllFilteredCodes(lookup)">
                  {{ 'TRANS__IMPORT__LOOKUPS__SHOW_ALL_MISSES' | translate: {count: listedMisses} }}
                </button>
              }
              @if (hidesHitCodes(lookup, check) || hidesFilteredCodes(lookup, check) && filter === 'all') {
                <button mat-button type="button" color="primary" class="lookup-definitions__show-all"
                        (click)="showAllCodes(lookup)">
                  {{ 'TRANS__IMPORT__LOOKUPS__SHOW_ALL_CODES' | translate: {count: check.codes.length} }}
                </button>
              }
            </div>
          </div>

          @if (topHits(lookup, check); as hits) {
            @if (hits.length) {
              <div class="lookup-definitions__box" [class.lookup-definitions__stale]="checkStale">
                <table class="lookup-definitions__codes lookup-definitions__top">
                  <thead>
                    <ng-container
                      *ngTemplateOutlet="codeTableHead; context: {$implicit: 'TRANS__IMPORT__LOOKUPS__TOP_CODES', labelColumns: checkedColumns}"
                    ></ng-container>
                  </thead>
                  <tbody>
                    @for (row of hits; track row.code) {
                      <ng-container
                        *ngTemplateOutlet="hitRow; context: {$implicit: row, labelColumns: checkedColumns}"
                      ></ng-container>
                    }
                  </tbody>
                </table>
              </div>
            }
          }
        }

        <div class="lookup-definitions__box">
          <button type="button" class="lookup-definitions__key-options-toggle"
                  [attr.aria-expanded]="isKeyOptionsOpen(lookup)" (click)="toggleKeyOptions(lookup)">
            <mat-icon aria-hidden="true">{{ isKeyOptionsOpen(lookup) ? 'expand_more' : 'chevron_right' }}</mat-icon>
            <span class="lookup-definitions__key-options-text">
              <span class="lookup-definitions__box-title">{{ 'TRANS__IMPORT__LOOKUP_KEY_OPTIONS' | translate }}</span>
              <span class="lookup-definitions__muted">{{ 'TRANS__IMPORT__LOOKUPS__KEY_OPTIONS_HINT' | translate }}</span>
            </span>
            <span class="lookup-definitions__muted lookup-definitions__key-options-state">
              {{ (keyOptionsInUse(lookup) ? 'TRANS__IMPORT__LOOKUPS__KEY_OPTIONS_USED' : 'TRANS__IMPORT__LOOKUPS__KEY_OPTIONS_UNUSED') | translate }}
            </span>
          </button>
          @if (isKeyOptionsOpen(lookup)) {
            <app-lookup-key-options class="lookup-definitions__key-options" [controls]="lookup" [columns]="columns">
            </app-lookup-key-options>
          }
        </div>

        <div>
          <button mat-button type="button" class="lookup-definitions__remove" (click)="remove.emit(lookup)">
            <mat-icon>delete_outline</mat-icon>
            <span class="bold uppercase">{{ 'TRANS__IMPORT__LOOKUP_REMOVE' | translate }}</span>
          </button>
        </div>
      </div>
    }
  </div>
}

<ng-template #codeTableHead let-titleKey let-labelColumns="labelColumns">
  <tr>
    <th scope="col">{{ titleKey | translate }}</th>
    <th scope="col" class="lookup-definitions__count">{{ 'TRANS__IMPORT__LOOKUPS__ROWS' | translate }}</th>
    <th scope="col">{{ 'TRANS__IMPORT__LOOKUPS__TABLE_KEY' | translate }}</th>
    @for (column of labelColumns; track column) {
      <th scope="col" class="lookup-definitions__mono">{{ column }}</th>
    } @empty {
      <th scope="col"></th>
    }
    <th scope="col"></th>
  </tr>
</ng-template>

<ng-template #hitRow let-row let-labelColumns="labelColumns">
  <tr>
    <td class="lookup-definitions__mono lookup-definitions__code">{{ row.code }}</td>
    <td class="lookup-definitions__count">{{ row.count }}</td>
    <td class="lookup-definitions__mono">{{ row.table_key }}</td>
    @for (column of labelColumns; track column) {
      <td>{{ row.labels[column] }}</td>
    } @empty {
      <td></td>
    }
    <td>
      <span class="lookup-definitions__tag lookup-definitions__tag--ok">
        <mat-icon aria-hidden="true">check</mat-icon>
        {{ 'TRANS__IMPORT__LOOKUPS__HIT' | translate }}
      </span>
    </td>
  </tr>
</ng-template>
